import api from '../axios'
import type { AuthApi, LoginRequest, LoginResponse, MfaChallengeRequest, MfaChallengeResponse, MfaSetupResponse, WebAuthnRegisterOptions, WebAuthnLoginOptions } from '../types'
import type { User } from '@/types/entities'

export const authApi: AuthApi = {
    login: (data: LoginRequest) => {
        // Security: Never log passwords - only log email for debugging
        if (import.meta.env.DEV) {
            console.log('Login request:', { email: data.email, hasPassword: !!data.password })
        }
        return api.post<LoginResponse>('/auth/login', data)
    },

    checkMfa: (email: string) => {
        return api.post<{
            requiresPassword: boolean
            mfaMethods: string[]
            passwordlessAvailable: boolean
            webauthnNeedsSetup: boolean
            totpNeedsSetup: boolean
        }>('/auth/check-mfa', { email })
    },

    logout: () => {
        return api.post('/auth/logout')
    },

    me: () => {
        return api.get<{ user: User }>('/auth/me')
    },

    refresh: () => {
        return api.post('/auth/refresh')
    },

    // MFA
    // No email in the body: the server takes the account from the short-lived challenge token the
    // password step issued, so a code alone can never be somebody else's second factor.
    mfaChallenge: (data: MfaChallengeRequest) =>
        api.post<MfaChallengeResponse>('/auth/totp/challenge', { totpCode: data.code }),

    mfaSetup: () =>
        api.post<MfaSetupResponse>('/auth/totp/setup'),

    mfaConfirm: (data: MfaChallengeRequest) =>
        api.post('/auth/totp/confirm', data),

    // TOTP
    totpSetup: () =>
        api.post<MfaSetupResponse>('/auth/totp/setup'),

    totpInitialSetup: (email: string) =>
        api.post<MfaSetupResponse>('/auth/totp/initial-setup', { email }),

    // setupTicket: when this code is the FIRST one from a new authenticator, the signed ticket the
    // server handed out with the QR code. The server stores the secret only when that first code
    // is right, so it has to be given the ticket back to know which secret is being proved.
    totpVerify: (data: { token: string; setupTicket?: string }) =>
        api.post<MfaChallengeResponse>('/auth/totp/verify', { code: data.token, ...(data.setupTicket ? { setupTicket: data.setupTicket } : {}) }),

    // WebAuthn
    webauthnRegisterOptions: () =>
        api.post<WebAuthnRegisterOptions>('/auth/webauthn/register/options'),

    webauthnRegisterVerify: (data: any) =>
        api.post('/auth/webauthn/register/verify', data),

    webauthnLoginOptions: () =>
        api.post<WebAuthnLoginOptions>('/auth/webauthn/login/options'),

    webauthnLoginVerify: (data: any) =>
        api.post<{ user: User }>('/auth/webauthn/login/verify', data),
}
